/**
 * Fehlerseiten für Web und Mobil: 404, 403, 500, 503.
 *
 * Eine Hülle (`FehlerSeite`), vier Ausprägungen. Gestaltet nur mit den
 * Token beider Oberflächen (`--text`, `--text-muted`, `--primary-text`,
 * `.btn`), damit Farbprofil und Hell/Dunkel mitlaufen.
 */

import { Component, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

/** Grundpfad der Anwendung – unter einem Unterpfad ist „/“ nicht die Startseite. */
const startseite = () => import.meta.env.BASE_URL || '/';

export function FehlerSeite({ code, title, children }: { code: string; title: string; children?: ReactNode }) {
  return (
    <div style={{ display: 'grid', placeItems: 'center', padding: '56px 16px', textAlign: 'center' }} role="alert">
      <div style={{ maxWidth: 460 }}>
        <div style={{ fontSize: 76, fontWeight: 800, lineHeight: 1, color: 'var(--primary-text, var(--primary))', letterSpacing: '-0.04em' }}>{code}</div>
        <h1 style={{ margin: '12px 0 6px', fontSize: 22 }}>{title}</h1>
        {children}
      </div>
    </div>
  );
}

const Text = ({ children }: { children: ReactNode }) => (
  <p style={{ margin: '0 0 18px', color: 'var(--text-muted)' }}>{children}</p>
);

/** 404 – mit Weiterleitung auf die Startseite nach `seconds` Sekunden. */
export function NotFoundPage({ seconds = 5 }: { seconds?: number }) {
  const navigate = useNavigate();
  const [left, setLeft] = useState(seconds);

  useEffect(() => {
    if (left <= 0) {
      navigate('/', { replace: true });
      return;
    }
    const timer = setTimeout(() => setLeft((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [left, navigate]);

  return (
    <FehlerSeite code="404" title="Seite nicht gefunden">
      <Text>
        Diese Adresse gibt es nicht (mehr). Sie werden in <strong aria-live="polite">{left}</strong>{' '}
        {left === 1 ? 'Sekunde' : 'Sekunden'} zur Startseite weitergeleitet.
      </Text>
      <button className="btn primary" onClick={() => navigate('/', { replace: true })}>
        Jetzt zur Startseite
      </button>
    </FehlerSeite>
  );
}

/** 403 – das Konto darf diesen Bereich nicht öffnen. */
export function ForbiddenPage({ detail }: { detail?: ReactNode }) {
  const navigate = useNavigate();
  return (
    <FehlerSeite code="403" title="Kein Zugriff">
      <Text>{detail ?? 'Für diesen Bereich fehlt Ihnen die Berechtigung. Wenden Sie sich bei Bedarf an die Administration.'}</Text>
      <button className="btn primary" onClick={() => navigate('/', { replace: true })}>
        Zur Startseite
      </button>
    </FehlerSeite>
  );
}

/** 503 – der Server antwortet nicht (Wartung, Neustart, Überlast). */
export function ServiceUnavailablePage() {
  return (
    <FehlerSeite code="503" title="Gerade nicht erreichbar">
      <Text>Der Server antwortet im Moment nicht – vermutlich ein Neustart oder eine Wartung. Bitte in Kürze erneut versuchen.</Text>
      <button className="btn primary" onClick={() => window.location.reload()}>
        Erneut versuchen
      </button>
    </FehlerSeite>
  );
}

/**
 * Meldet, ob eine Anfrage den Server nicht erreicht hat. Der API-Client löst
 * dazu das Ereignis `ff:unreachable` aus (Netzfehler bzw. 502/503/504). Solange
 * es gilt, fragt der Hook alle 5 Sekunden `/api/health` ab und hebt die
 * Meldung selbst auf, sobald der Server wieder antwortet.
 */
export function useUnreachable(): boolean {
  const [down, setDown] = useState(false);
  useEffect(() => {
    const on = () => setDown(true);
    window.addEventListener('ff:unreachable', on);
    return () => window.removeEventListener('ff:unreachable', on);
  }, []);
  useEffect(() => {
    if (!down) return;
    const timer = setInterval(() => {
      fetch(`${import.meta.env.BASE_URL || '/'}api/health`)
        .then((response) => response.ok && setDown(false))
        .catch(() => undefined);
    }, 5000);
    return () => clearInterval(timer);
  }, [down]);
  return down;
}

/** 500 – fängt einen Absturz der Oberfläche ab, statt eine weiße Fläche zu zeigen. */
export class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error(error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <FehlerSeite code="500" title="Das hat nicht geklappt">
        <Text>In der Anwendung ist ein Fehler aufgetreten. Ihre Eingaben auf dieser Seite sind möglicherweise nicht gespeichert.</Text>
        <div className="row" style={{ gap: 8, justifyContent: 'center' }}>
          <button className="btn primary" onClick={() => window.location.reload()}>
            Seite neu laden
          </button>
          <button className="btn" onClick={() => window.location.assign(startseite())}>
            Zur Startseite
          </button>
        </div>
      </FehlerSeite>
    );
  }
}
